import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import apiClient from "api/apiClient";
import SiteBannerView from "./SiteBannerView";

const API = process.env.REACT_APP_BASE_URL;
const REFRESH_MS = 10 * 60 * 1000;
// Fired by Admin → Announcements after a save or an end, so the admin sees the change at once.
export const BANNER_CHANGED_EVENT = "cdt:banner-changed";

// Pages a signed-out visitor sees (sign-in, registration, legal, payment returns): never a
// banner there (D11), even for a user who happens to be signed in.
const PUBLIC_PATHS = [
  /^\/$/, /^\/login/, /^\/register/, /^\/forgot-/, /^\/reset-password/, /^\/auth\//,
  /^\/legal\//, /^\/verify/, /^\/marketing-optin/, /^\/organization-invite/, /^\/payment-/,
];

// Closing is remembered in this browser per banner VERSION, so an edited banner shows again.
// Storage may be blocked (private window): the banner then simply shows again next load.
const dismissKey = (b) => `cdt:banner-dismissed:${b.id}:${b.version}`;
const isDismissed = (b) => {
  try {
    return window.localStorage.getItem(dismissKey(b)) === "1";
  } catch {
    return false;
  }
};
const rememberDismissed = (b) => {
  try {
    window.localStorage.setItem(dismissKey(b), "1");
  } catch {
    /* not remembered; harmless */
  }
};

// The announcement at the top of every signed-in page (Admin → Announcements). Mounted in the
// app shell, not in Layout, because the project view and the wizard render outside Layout.
// It publishes its height as --cdt-banner-h so full-height pages (the project view is exactly
// 100vh) can subtract it instead of being pushed off the bottom of the screen.
const SiteBanner = () => {
  const isAuthenticated = useSelector((state) => Boolean(state.auth?.isAuthenticated));
  const { pathname } = useLocation();
  const [banner, setBanner] = useState(null);
  const [closed, setClosed] = useState(false);
  const [, setTick] = useState(0);
  const ref = useRef(null);

  const onPublicPage = PUBLIC_PATHS.some((re) => re.test(pathname));

  const load = useCallback(async () => {
    try {
      const { data } = await apiClient.get(`${API}/banners/active`);
      setBanner(data?.banner || null);
    } catch {
      // Advisory only: a failed fetch must never put an error on every page.
    }
  }, []);

  useEffect(() => {
    if (!isAuthenticated) {
      setBanner(null);
      return undefined;
    }
    load();
    const t = setInterval(load, REFRESH_MS);
    const onFocus = () => load();
    window.addEventListener("focus", onFocus);
    window.addEventListener(BANNER_CHANGED_EVENT, onFocus);
    return () => {
      clearInterval(t);
      window.removeEventListener("focus", onFocus);
      window.removeEventListener(BANNER_CHANGED_EVENT, onFocus);
    };
  }, [isAuthenticated, load]);

  useEffect(() => {
    setClosed(banner ? isDismissed(banner) : false);
  }, [banner]);

  // Disappear at the end date without waiting for the next fetch.
  useEffect(() => {
    if (!banner?.endsAt) return undefined;
    const ms = new Date(banner.endsAt).getTime() - Date.now();
    if (!(ms > 0)) return undefined;
    const t = setTimeout(() => setTick((n) => n + 1), Math.min(ms + 500, 2 ** 31 - 1));
    return () => clearTimeout(t);
  }, [banner]);

  const ended = banner?.endsAt ? new Date(banner.endsAt).getTime() <= Date.now() : false;
  const visible = Boolean(isAuthenticated && banner && !closed && !ended && !onPublicPage);

  useLayoutEffect(() => {
    const root = document.documentElement;
    if (!visible || !ref.current) {
      root.style.setProperty("--cdt-banner-h", "0px");
      return undefined;
    }
    const set = () => root.style.setProperty("--cdt-banner-h", `${ref.current?.offsetHeight || 0}px`);
    set();
    const ro = typeof ResizeObserver !== "undefined" ? new ResizeObserver(set) : null;
    ro?.observe(ref.current);
    return () => {
      ro?.disconnect();
      root.style.setProperty("--cdt-banner-h", "0px");
    };
  }, [visible]);

  if (!visible) return null;
  return (
    <div ref={ref}>
      <SiteBannerView
        banner={banner}
        onClose={() => {
          rememberDismissed(banner);
          setClosed(true);
        }}
      />
    </div>
  );
};

export default SiteBanner;
